import { AxiosResponse } from 'axios';
import { apiV2 } from './http';
import type {
  Cashback,
  CashbackListQuery,
  CashbackListResponse,
  UpdateCashbackPaymentPayload,
} from './interfaces/cashback';

const BASE_PATH = 'cashback/redemptions';

function buildQueryParams(query: CashbackListQuery): Record<string, string> {
  const params: Record<string, string> = {
    page: String(query.page),
    perPage: String(query.perPage),
    sortBy: query.sortBy,
    sortOrder: query.sortOrder,
  };
  if (query.status) params.status = query.status;
  if (query.search?.trim()) params.search = query.search.trim();
  if (query.start_date) params.start_date = query.start_date;
  if (query.end_date) params.end_date = query.end_date;
  return params;
}

function normalizeRow(raw: Record<string, unknown>): Cashback {
  const rawId = raw.id ?? raw.idi;
  const id = typeof rawId === 'number' ? rawId : Number(rawId);
  const idi = raw.idi != null ? Number(raw.idi) : 0;
  return {
    id,
    idi,
    requested_at: String(raw.requested_at),
    company_name: String(raw.company_name),
    document: String(raw.document),
    pix_key: String(raw.pix_key),
    cashback_amount: Number(raw.cashback_amount),
    paid: Boolean(raw.paid),
    payment_date: raw.payment_date ? String(raw.payment_date) : undefined,
  };
}

function normalizeListResponse(body: unknown): CashbackListResponse {
  const o = body as {
    data: Record<string, unknown>[];
    meta: CashbackListResponse['meta'];
  };
  return {
    data: (o.data ?? []).map(normalizeRow),
    meta: o.meta,
  };
}

export async function listCashbacks(
  query: CashbackListQuery,
  signal?: AbortSignal,
): Promise<CashbackListResponse> {
  const res: AxiosResponse<CashbackListResponse> = await apiV2.get(BASE_PATH, {
    params: buildQueryParams(query),
    signal,
  });
  return normalizeListResponse(res.data);
}

export async function updateCashbackPayment(
  id: number,
  payload: UpdateCashbackPaymentPayload,
  signal?: AbortSignal,
): Promise<Cashback> {
  const res: AxiosResponse<Record<string, unknown>> = await apiV2.patch(
    `${BASE_PATH}/${id}/payment`,
    payload,
    { signal },
  );
  return normalizeRow(res.data);
}
